/**
 * S-05 — Catalogue Transversal Skills (Admin) — US-08, Lot 3b.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  transversalApi,
  ApiRequestError,
  type TransversalImportResult,
  type TransversalSkillDto,
  type TransversalVersionDto,
} from "../api/api";

export function TransversalSkillsPage() {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<TransversalVersionDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [skills, setSkills] = useState<TransversalSkillDto[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [label, setLabel] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<TransversalImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    macroProcess: "",
    businessProcess: "",
    subProcess: "",
    task: "",
    complexityIndex: 3,
  });

  async function refresh(preferId?: string | null) {
    setError(null);
    try {
      const verRes = await transversalApi.listVersions();
      setVersions(verRes.versions);
      const next =
        preferId ??
        verRes.versions.find((v) => v.id === selectedId)?.id ??
        verRes.versions.find((v) => v.active)?.id ??
        verRes.versions[0]?.id ??
        null;
      setSelectedId(next);
      if (next) {
        const sk = await transversalApi.listSkills(next, includeArchived);
        setSkills(sk.skills);
      } else {
        setSkills([]);
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSkills([]);
      return;
    }
    void transversalApi
      .listSkills(selectedId, includeArchived)
      .then((r) => setSkills(r.skills))
      .catch((err) => {
        setError(err instanceof ApiRequestError ? err.message : t("load_skills_failed"));
      });
  }, [selectedId, includeArchived]);

  async function downloadTemplate() {
    try {
      const blob = await transversalApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "skillmap-transversal-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function onImport(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError(t("choose_xlsx"));
      return;
    }
    if (!label.trim()) {
      setError(t("version_label_required"));
      return;
    }
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const result = await transversalApi.import(file, label.trim());
      setReport(result);
      setFile(null);
      setLabel("");
      await refresh(result.version.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onActivate(id: string) {
    if (!window.confirm(t("confirm_activate"))) return;
    setBusy(true);
    setError(null);
    try {
      await transversalApi.activate(id);
      await refresh(id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("activate_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await transversalApi.createSkill({
        versionId: selectedId,
        macroProcess: form.macroProcess.trim(),
        businessProcess: form.businessProcess.trim(),
        subProcess: form.subProcess.trim() || null,
        task: form.task.trim() || null,
        complexityIndex: form.complexityIndex,
      });
      setForm({
        macroProcess: "",
        businessProcess: "",
        subProcess: "",
        task: "",
        complexityIndex: 3,
      });
      await refresh(selectedId);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("create_failed"));
    } finally {
      setBusy(false);
    }
  }

  async function onArchive(id: string) {
    setBusy(true);
    setError(null);
    try {
      await transversalApi.archiveSkill(id);
      await refresh(selectedId);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("tv_catalog_title")}</h1>
          <p>{t("transversal_skills")}</p>
        </div>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-form-card">
        <h2>{t("new_import")}</h2>
        <button type="button" className="sm-btn sm-btn-secondary" onClick={() => void downloadTemplate()}>
          ⬇ {t("download_excel_template")}
        </button>
        <form onSubmit={onImport}>
          <div className="sm-form-actions" style={{ flexWrap: "wrap" }}>
            <input
              className="sm-input"
              required
              placeholder={t("version_label_ph")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
              {t("import")}
            </button>
          </div>
        </form>
        {report ? (
          <p className="sm-muted">
            {t("import")} — {report.imported}
          </p>
        ) : null}
      </div>

      <div className="sm-section">
        <h2>{t("versions")}</h2>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("version_label")}</th>
                <th>{t("imported_on")}</th>
                <th>{t("transversal_skills")}</th>
                <th>{t("status")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr
                  key={v.id}
                  style={{
                    background: selectedId === v.id ? "rgba(31,78,121,0.08)" : undefined,
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedId(v.id)}
                >
                  <td>{v.label}</td>
                  <td>{new Date(v.importedAt).toLocaleString()}</td>
                  <td>{v.skillCount}</td>
                  <td>
                    {v.active ? (
                      <span className="sm-badge">{t("active")}</span>
                    ) : (
                      t("status_inactive")
                    )}
                  </td>
                  <td>
                    {!v.active ? (
                      <button
                        type="button"
                        className="sm-btn sm-btn-primary"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          void onActivate(v.id);
                        }}
                      >
                        {t("activate")}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedId ? (
        <>
          <div className="sm-form-card">
            <h2>{t("add")}</h2>
            <form onSubmit={onCreate}>
              <div className="sm-form-actions" style={{ flexWrap: "wrap" }}>
                <input
                  className="sm-input"
                  required
                  placeholder={t("tv_macro_ph")}
                  value={form.macroProcess}
                  onChange={(e) => setForm({ ...form, macroProcess: e.target.value })}
                />
                <input
                  className="sm-input"
                  required
                  placeholder={t("tv_bp_ph")}
                  value={form.businessProcess}
                  onChange={(e) => setForm({ ...form, businessProcess: e.target.value })}
                />
                <input
                  className="sm-input"
                  placeholder={t("tv_sub_ph")}
                  value={form.subProcess}
                  onChange={(e) => setForm({ ...form, subProcess: e.target.value })}
                />
                <input
                  className="sm-input"
                  placeholder={t("tv_task_ph")}
                  value={form.task}
                  onChange={(e) => setForm({ ...form, task: e.target.value })}
                />
                <input
                  className="sm-input"
                  type="number"
                  min={1}
                  max={10}
                  value={form.complexityIndex}
                  onChange={(e) => setForm({ ...form, complexityIndex: Number(e.target.value) })}
                  style={{ width: 80 }}
                />
                <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
                  {t("add")}
                </button>
              </div>
            </form>
          </div>

          <div className="sm-section">
            <div className="sm-toolbar" style={{ justifyContent: "space-between", marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>Skills</h2>
              <label className="sm-muted">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />{" "}
                {t("tv_include_archived")}
              </label>
            </div>
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>Macro</th>
                    <th>Business</th>
                    <th>Sub</th>
                    <th>Task</th>
                    <th>Cplx</th>
                    <th>{t("status")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map((s) => (
                    <tr key={s.id}>
                      <td>{s.macroProcess}</td>
                      <td>{s.businessProcess}</td>
                      <td>{s.subProcess ?? "—"}</td>
                      <td>{s.task ?? "—"}</td>
                      <td>{s.complexityIndex}</td>
                      <td>{s.isArchived ? t("inactive") : t("active")}</td>
                      <td>
                        {!s.isArchived ? (
                          <button
                            type="button"
                            className="sm-btn sm-btn-ghost"
                            disabled={busy}
                            onClick={() => void onArchive(s.id)}
                          >
                            {t("archive")}
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
